import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Modal, Radio } from 'antd';
import { Button } from '@cropler/ui-kit';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import styles from './styles.module.scss';
import { Icon as CroplerIcon } from '@cropler/ui-kit';

import { Header } from '../PageLayout/index.jsx';
import SearchBar from '../SearchBar/index.jsx';
import { DevicesAddForm, DevicesDiagram, DevicesEmptyView, DevicesTable } from './index.jsx';
import AddPeriodForm from '../AddPeriodForm/index.jsx';
import {
  sensorsApi,
  useGetSensorsQuery,
  useGetLocationsQuery,
  useUpdateSensorMutation,
} from '../../store/api/sensors';
import { InitialEditLocationFields } from '../../constants/data';
import { QRCodeDeviceId } from '../../constants/keys';
import { useViewport } from '../../hooks';
import { BreakpointMobile, BreakpointMobileUp, PathnamesMap } from '../../constants/ui';
import { selectBoundary } from '../../store/slices/sensors';
import { useGetFieldsQuery } from '../../store/api/fields';
import { isDateInPeriod } from '../../utils/dates';
import { DevicesGetSeasonData } from './lib/index.jsx';
import { EmptyViewSensors } from '../EmptyView/index.jsx';
import { DevicesQRCodeLinkKey } from './constants/index.jsx';
import { getSidebarLinksMap } from '../../utils/ui';
import RequestButton from '../GlobalComponents/RequestButton.jsx';
import DevicesRenameForm from './DevicesRenameForm.jsx';

dayjs.extend(utc);

const Devices = ({ type }) => {
  const { t } = useTranslation();
  const SidebarLinksMap = getSidebarLinksMap(t);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params, setSearchParams] = useSearchParams();
  const { widthViewport } = useViewport();
  const location = useLocation();

  const { currentWorkspaceId } = useSelector((state) => state.workspaces);

  const [typePage, setTypePage] = useState(type || 'table');
  const [activeAddPeriodPanel, setActiveAddPeriodPanel] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddDeviceModalOpen, setIsAddDeviceOpen] = useState(false);

  const [sensorId, setSensorId] = useState('');
  const [sensorName, setSensorName] = useState('');
  const [sensorCurrentPeriods, setSensorCurrentPeriods] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(InitialEditLocationFields);
  const [sensorsData, setSensorsData] = useState([]);
  const [currentUpdateSensor, setCurrentUpdateSensor] = useState('');
  const [showEmptyState, setShowEmptyState] = useState(false);

  const {
    sorting: { dir },
    sortingDevices: { paramDevices },
  } = useSelector((state) => state.sensors);
  const { currentUserRole } = useSelector((state) => state.auth);

  const {
    data: sensors,
    isFetching: isSensorsLoading,
    isSuccess: isSensorsSuccess,
  } = useGetSensorsQuery(
    { ordering: `${dir}${paramDevices}`, query: params.get('search') },
    { skip: !currentWorkspaceId, refetchOnMountOrArgChange: true },
  );

  const { data: locations, isSuccess: isLocationsSuccess } = useGetLocationsQuery(
    {},
    { skip: !currentWorkspaceId, refetchOnMountOrArgChange: true },
  );

  const { data: fields, isSuccess: isFieldsSuccess } = useGetFieldsQuery(
    {},
    { skip: !currentWorkspaceId, refetchOnMountOrArgChange: true },
  );

  const [updateSensor, { isSuccess: isUpdateSensorSuccess }] = useUpdateSensorMutation();

  const [formRename] = Form.useForm();

  const onOpenRenameModal = ({ id, name }) => {
    setCurrentUpdateSensor(id);
    setIsEditModalOpen(true);
    formRename.setFieldValue('deviceName', name);
  };

  const onUpdateSensor = () => {
    const sensorId = currentUpdateSensor;
    const name = formRename.getFieldValue('deviceName');
    if (sensorId && name) {
      updateSensor({ sensorId, name });
    }
  };

  const onAddSensor = ({ id, name }) => {
    if (id && name) {
      setSensorName(name);
      setSensorId(id);
      setIsAddDeviceOpen(false);
      setActiveAddPeriodPanel(true);
    }
  };

  const onOpenAddPeriod = ({ id, name }) => {
    let periods = [];
    dispatch(selectBoundary(''));
    locations.map((item) => {
      if (item.sensor.id === id) {
        periods.push([item.install_date, item.removal_date]);
      }
    });
    setSensorCurrentPeriods(periods);
    setSelectedLocation(InitialEditLocationFields);
    setSensorId(id);
    setSensorName(name);
    setActiveAddPeriodPanel(true);
  };

  const onOpenEditPeriod = (data) => {
    setSelectedLocation({
      locationId: data.id,
      seasonId: data.field_season_id,
      name: data.name,
      install: data.install_date,
      removal: data.removal_date,
      coordinates: data.install_point.coordinates ? data.install_point.coordinates : data.sensor.gps_point.coordinates,
      // coordinates: data.gps_point ? data.gps_point.coordinates : data.install_point.coordinates,
    });
    setSensorId(data.sensor_id);
    setSensorName(data.sensor.name);
    setActiveAddPeriodPanel(true);
  };

  const onChangeView = (e) => {
    const value = e.target.value;
    navigate({
      pathname: `/${value === 'table' ? PathnamesMap.devices : PathnamesMap.devicesDiagram}`,
    });
  };

  const onCloseDeviceAddModal = () => {
    localStorage.removeItem(QRCodeDeviceId);
    setIsAddDeviceOpen(false);

    const nextParams = new URLSearchParams(params);
    nextParams.delete(DevicesQRCodeLinkKey);
    setSearchParams(nextParams, { replace: true });
  }

  useEffect(() => {
    const isAddDeviceActive = params.get(DevicesQRCodeLinkKey);
    if (isAddDeviceActive !== null) {
      setIsAddDeviceOpen(true);
    }
  }, [params]);

  useEffect(() => {
    if (location.pathname === `/${PathnamesMap.devices}`) {
      setTypePage('table');
    } else if (location.pathname === `/${PathnamesMap.devicesDiagram}`) {
      setTypePage('diagram');
    }
  }, [location]);

  useEffect(() => {
    if (!isFieldsSuccess || !isLocationsSuccess || !isSensorsSuccess || isSensorsLoading) {
      return;
    }

    // Locations data
    const fullLocationsData = [];
    locations.map((location) => {
      const fieldData = DevicesGetSeasonData({ fields, id: location.field_season_id });
      fullLocationsData.push({
        ...location,
        field: fieldData && fieldData.field ? fieldData.field : '',
      });
    });

    // Sensors + locations + fields data
    if (sensors && sensors.length) {
      const fullSensorsData = [];
      sensors.map((sensor) => {
        let location = null;
        const sensorLocations = fullLocationsData.filter((item) => item.sensor.id === sensor.id);
        if (sensorLocations && sensorLocations.length > 0) {
          sensorLocations.map((el) => {
            if (isDateInPeriod(el.install_date, el.removal_date, dayjs())) {
              location = { ...el };
            }
          });
        }

        const soilSensor = sensor.settings.enabled.filter((item) => item.name === 'pair_soil_sensor');

        fullSensorsData.push({
          id: sensor.id,
          name: sensor.name,
          device_id: sensor.device_id,
          synced_at: sensor.synced_at,
          location,
          pastLocations: location ? sensorLocations.filter((item) => item.id !== location.id) : sensorLocations,
          isSoilSensorAllowed: sensor.settings.allowed.includes('pair_soil_sensor'),
          isSoilSensorSupported: sensor.settings.supported.includes('pair_soil_sensor'),
          soilSensor: soilSensor && soilSensor.length > 0 ? soilSensor[0] : null,
        });
      });
      setSensorsData(fullSensorsData);
      setShowEmptyState(false);
    }

    if (sensors && sensors.length === 0) {
      setShowEmptyState(true);
    }
  }, [isFieldsSuccess, isLocationsSuccess, isSensorsSuccess, sensors]);

  useEffect(() => {
    if (isUpdateSensorSuccess) {
      setIsEditModalOpen(false);
      dispatch(sensorsApi.util.resetApiState());
    }
  }, [isUpdateSensorSuccess]);

  // if (isSensorsLoading) {
  // return <Loading />
  // }

  return (
    <>
      <Header title={SidebarLinksMap.devices.title} t={t} />
      <main className="main">
        {sensors && sensors.length === 0 && !params && (
          <DevicesEmptyView addDevice={() => setIsAddDeviceOpen(true)} t={t} />
        )}

        <div className={styles.devices}>
          <div className={styles.devicesHeader}>
            <div className={styles.filter}>
              {sensorsData && sensorsData.length > 0 && (
                <>
                  <SearchBar placeholder="Search" size={widthViewport < BreakpointMobileUp ? 'large' : null} t={t} />
                  {widthViewport > BreakpointMobile && (
                    <div key={typePage}>
                      <Radio.Group defaultValue={typePage} onChange={onChangeView}>
                        <Radio.Button value="table">
                          <CroplerIcon name="table" width={16} height={16} />
                          <span>{t('devices.tabs.table')}</span>
                        </Radio.Button>
                        <Radio.Button value="diagram">
                          <CroplerIcon name="diagram" width={16} height={16} />
                          <span>{t('devices.tabs.diagram')}</span>
                        </Radio.Button>
                      </Radio.Group>
                    </div>
                  )}
                </>
              )}

              {/*<Button type="secondary" size="small" icon={<SortDownIcon />} onClick={() => setActiveSortingPanel(true)}>*/}
              {/*  {widthViewport > BreakpointMobile && 'Sort'}*/}
              {/*</Button>*/}
            </div>
            {(currentUserRole === 'owner' || currentUserRole === 'editor') && (
              <div className={styles.filter}>
                <RequestButton t={t} hideTextOnMobile={true} />
                {sensorsData && sensorsData.length > 0 && (
                  <Button
                    type="primary"
                    icon={<CroplerIcon name="add" />}
                    size={widthViewport < BreakpointMobileUp ? 'large' : null}
                    onClick={() => setIsAddDeviceOpen(true)}
                  >
                    {widthViewport > BreakpointMobile && t('devices.addDevice')}
                  </Button>
                )}
              </div>
            )}
          </div>

          {sensorsData && sensorsData.length > 0 && (
            <>
              {(typePage === 'table' || widthViewport < BreakpointMobileUp) && (
                <DevicesTable
                  sensors={sensors}
                  sensorsData={sensorsData}
                  onClickEdit={(data) => onOpenRenameModal({ id: data.id, name: data.name })}
                  onClickAddLocation={(item) => onOpenAddPeriod({ id: item.id, name: item.name })}
                  onClickEditLocation={(location) => onOpenEditPeriod(location)}
                  t={t}
                />
              )}

              {typePage === 'diagram' && widthViewport > BreakpointMobile && (
                <DevicesDiagram
                  sensorsData={sensorsData}
                  onClickEditLocation={(location) => onOpenEditPeriod(location)}
                  onAddLocation={(location) => onOpenAddPeriod({ id: location.id, name: location.name })}
                  t={t}
                />
              )}
            </>
          )}

          {showEmptyState && (
            <div className={styles.devicesEmpty}>
              <EmptyViewSensors onClickButton={() => setIsAddDeviceOpen(true)} t={t} />
            </div>
          )}
        </div>
      </main>

      {/*Add/Edit Location*/}
      <Modal
        title={`${t('locations.addPeriod.addFor')} ${sensorName}`}
        open={activeAddPeriodPanel}
        onCancel={() => setActiveAddPeriodPanel(false)}
        footer={false}
        centered
        width={700}
        className="mobile-hide-modal"
      >
        <AddPeriodForm
          key={selectedLocation.locationId || sensorId}
          closePanel={() => setActiveAddPeriodPanel(false)}
          sensorId={sensorId}
          disabledPeriods={sensorCurrentPeriods}
          initialData={selectedLocation}
          t={t}
        />
      </Modal>

      {/*Rename*/}
      <Modal
        title={t('devices.rename')}
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        footer={false}
        centered
        width={450}
      >
        <DevicesRenameForm
          t={t}
          form={formRename}
          onClickCancel={() => setIsEditModalOpen(false)}
          onClickSave={() => onUpdateSensor()}
        />
      </Modal>

      {/*Add Device*/}
      <Modal
        open={isAddDeviceModalOpen}
        onCancel={() => onCloseDeviceAddModal()}
        footer={false}
        centered
        width={700}
        className="modalNoPadding mobile-hide-modal"
      >
        {isAddDeviceModalOpen && (
          <DevicesAddForm
            closePanel={onCloseDeviceAddModal}
            clickNext={({ id, name }) => onAddSensor({ id, name })}
            t={t}
          />
        )}
      </Modal>
    </>
  );
};

export default Devices;
